import React from 'react';
import { ScrollView, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import styled from 'styled-components/native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { AppBar, Card, Text, Button } from '@ui';
import { useTheme } from '@theme/ThemeProvider';

const Container = styled.View`
  flex: 1;
  background-color: ${({ theme }) => theme.colors.background};
`;

const Content = styled(ScrollView)`
  flex: 1;
`;

const Section = styled.View`
  padding: ${({ theme }) => theme.spacing.base}px;
`;

const TrimesterCard = styled.View`
  background-color: #fff0f5;
  border-radius: ${({ theme }) => theme.radius.lg}px;
  padding: ${({ theme }) => theme.spacing.xl}px;
  margin-bottom: ${({ theme }) => theme.spacing.xl}px;
  position: relative;
  overflow: hidden;
`;

const TrimesterHeader = styled.View`
  margin-bottom: ${({ theme }) => theme.spacing.base}px;
`;

const WeekTag = styled.View`
  background-color: ${({ theme }) => theme.colors.brandPrimary};
  border-radius: 20px;
  padding: ${({ theme }) => theme.spacing.xs}px ${({ theme }) => theme.spacing.base}px;
  align-self: flex-start;
  margin-top: ${({ theme }) => theme.spacing.sm}px;
`;

const TrimesterFooter = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
  margin-top: ${({ theme }) => theme.spacing.xl}px;
`;

const BabyIconsContainer = styled.View`
  flex-direction: row;
  gap: ${({ theme }) => theme.spacing.sm}px;
  margin-bottom: ${({ theme }) => theme.spacing.sm}px;
`;

const BabyIconCircle = styled.View`
  width: 48px;
  height: 48px;
  border-radius: 24px;
  background-color: white;
  align-items: center;
  justify-content: center;
`;

const DotsGrid = styled.View`
  width: 24px;
  height: 24px;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 4px;
`;

const Dot = styled.View`
  width: 4px;
  height: 4px;
  border-radius: 2px;
  background-color: ${({ theme }) => theme.colors.brandPrimary};
`;

const SectionHeader = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.md}px;
`;

const HealthTipCard = styled(Card)`
  flex-direction: row;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.md}px;
  padding: ${({ theme }) => theme.spacing.base}px;
`;

const TipIconWrapper = styled.View`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.radius.md}px;
  background-color: ${({ theme }) => theme.colors.neutral100 || '#F5F5F5'};
  align-items: center;
  justify-content: center;
  margin-right: ${({ theme }) => theme.spacing.base}px;
`;

const TipContent = styled.View`
  flex: 1;
`;

const DidYouKnowCard = styled(Card)`
  flex-direction: row;
  align-items: flex-start;
  padding: ${({ theme }) => theme.spacing.base}px;
`;

const LightbulbIcon = styled.View`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.radius.md}px;
  background-color: ${({ theme }) => theme.colors.errorLight || '#FFEBEE'};
  align-items: center;
  justify-content: center;
  margin-right: ${({ theme }) => theme.spacing.base}px;
`;

const PregnancyTipsScreen: React.FC = () => {
  const theme = useTheme();
  const navigation = useNavigation();

  const healthTips = [
    {
      id: '1',
      icon: '🍼',
      title: 'Milk',
      description: 'Vital for bone health and development',
    },
    {
      id: '2',
      icon: '🍌',
      title: 'Bananas',
      description: 'Helps improve digestion',
    },
  ];

  return (
    <Container>
      <AppBar
        title="Pregnancy Tips"
        leftIcon="arrow-back"
        onLeftPress={() => navigation.goBack()}
      />
      <Content showsVerticalScrollIndicator={false}>
        <Section>
          {/* 1st Trimester Card */}
          <TrimesterCard>
            <TrimesterHeader>
              <Text
                variant="headline"
                color="textPrimary"
                style={{ fontSize: 32, fontWeight: 'bold', marginBottom: 8 }}
              >
                1st Trimester
              </Text>
              <WeekTag>
                <Text variant="caption" style={{ color: theme.colors.textOnPrimary }}>
                  8 weeks and 10 days
                </Text>
              </WeekTag>
            </TrimesterHeader>
            <TrimesterFooter>
              <View>
                <BabyIconsContainer>
                  <BabyIconCircle>
                    <DotsGrid>
                      {Array.from({ length: 9 }).map((_, i) => (
                        <Dot key={i} />
                      ))}
                    </DotsGrid>
                  </BabyIconCircle>
                  <BabyIconCircle>
                    <Icon name="child-care" size={24} color={theme.colors.brandPrimary} />
                  </BabyIconCircle>
                </BabyIconsContainer>
                <Text
                  variant="bodyMedium"
                  color="textPrimary"
                  style={{ fontWeight: '600', marginTop: 4 }}
                >
                  Size of a raspberry!
                </Text>
              </View>
            </TrimesterFooter>
          </TrimesterCard>

          {/* Health Tips Section */}
          <SectionHeader>
            <Text variant="title" color="textPrimary" style={{ fontWeight: 'bold' }}>
              Health Tips
            </Text>
            <Button
              variant="ghost"
              size="sm"
              onPress={() => {
                // Navigate to more tips or external link
              }}
              style={{ padding: 0 }}
            >
              <Text variant="bodyMedium" style={{ color: theme.colors.brandPrimary }}>
                Learn more &gt;
              </Text>
            </Button>
          </SectionHeader>

          {healthTips.map(tip => (
            <HealthTipCard key={tip.id} padding="none">
              <TipIconWrapper>
                <Text style={{ fontSize: 24 }}>{tip.icon}</Text>
              </TipIconWrapper>
              <TipContent>
                <Text variant="titleMedium" color="textPrimary" style={{ fontWeight: 'bold' }}>
                  {tip.title}
                </Text>
                <Text variant="bodySmall" color="textSecondary" style={{ marginTop: 4 }}>
                  {tip.description}
                </Text>
              </TipContent>
            </HealthTipCard>
          ))}

          {/* Did you know Section */}
          <Text
            variant="title"
            color="textPrimary"
            style={{ fontWeight: 'bold', marginTop: 24, marginBottom: 16 }}
          >
            Did you know?
          </Text>

          <DidYouKnowCard padding="none">
            <LightbulbIcon>
              <Icon name="lightbulb" size={24} color={theme.colors.brandPrimary} />
            </LightbulbIcon>
            <View style={{ flex: 1 }}>
              <Text variant="body" color="textSecondary" style={{ lineHeight: 22 }}>
                Research has shown that coffee consumption of less than 200g daily during pregnancy
                is generally considered safe and does not increase the risk of miscarriage or
                preterm birth.
              </Text>
            </View>
          </DidYouKnowCard>

          <View style={{ height: 24 }} />
        </Section>
      </Content>
    </Container>
  );
};

export default PregnancyTipsScreen;
